<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5">
    <div class="flex gap-x-3 items-start">
      <FormSelectMultiBadge
        v-model="payload.from"
        :label="$t('evaluate.email_from')"
        :options="emailFromList"
        searchable
        :clearable="false"
        :class="fromIsDomain ? 'w-1/2' : 'w-full'"
        @update:modelValue="emitPayload"
      ></FormSelectMultiBadge>
      <div v-if="fromIsDomain" class="w-1/2 flex flex-row items-center">
        <FormInput v-model="payload.fromEmail" name="from_em" label="From Email" class="flex-grow" @update:modelValue="emitPayload" />
        <div class="text-13 mt-4 ml-1 whitespace-nowrap">@{{ payload.from.value }}</div>
      </div>
    </div>
    <PhraseEditor v-if="!isAgent" :label="$t('evaluate.email_to_comma')" v-model="emailTo" :items="variables" class="mt-3 w-full" @update:modelValue="onEmailToChange" />
    <FormSelect
      :label="$t('evaluate.email_template')"
      class="mt-3 w-full"
      v-model="payload.email_template"
      :options="emailTemplates"
      default-reducer
      :clearable="false"
      @update:modelValue="emitPayload"
    ></FormSelect>
    <VariableSelect
      v-if="!isAgent"
      v-model="payload.data_binding"
      multiple
      :label="$t('evaluate.email_data')"
      class="mt-3 flex-grow"
      selectBgColor="bg-white"
      :filter-data-type="['Text', 'Number', 'Email', 'YesNo', 'Chart', 'DataTable']"
      :showCreate="false"
      @update:modelValue="emitPayload"
    />
    <VariableSelect
      v-if="!isAgent"
      v-model="payload.attachments"
      multiple
      :label="$t('evaluate.attachments')"
      class="mt-3 flex-grow"
      selectBgColor="bg-white"
      :filter-data-type="['Image', 'PDF', 'Excel']"
      :showCreate="false"
      @update:modelValue="emitPayload"
    />
    <div v-if="isAgent" class="text-11 text-gray-400 mt-3">The agent fills the recipients, body content, and attachments at runtime. You set the sender and template.</div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import debounce from '../../../helpers/debounce';
import uuidv4 from '../../../helpers/uuid';
import EvaluateItemsConfig from './EvaluateItemsConfig';

export default {
  name: 'EmailConfig',
  props: {
    item: {
      type: Object,
      default: null,
    },
    isAgent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      from: null,
      emailTo: '',
      payload: structuredClone(EvaluateItemsConfig['action-send-email'].payload),
    };
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectTemplates', 'getProjectEmailProviders']),
    variables() {
      let variables = [];
      if (this.projectId && Object.keys(this.projectData).length) {
        variables = Object.keys(this.projectData?.[this.projectId]?.variables || {}).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) || [];
      }
      variables = variables.map((variable) => ({
        id: uuidv4(),
        value: variable,
      }));
      return variables;
    },
    projectId() {
      return this.$route.params.projectId;
    },
    fromIsDomain() {
      return this.payload.from && this.payload.from.badge1 === 'domain';
    },
    emailTemplates() {
      return Object.keys(this.getProjectTemplates()).map((key) => ({
        label: this.getProjectTemplates()[key].name,
        value: key,
      }));
    },
    emailFromList() {
      const list = Object.values(this.getProjectEmailProviders()).reduce((acc, item) => {
        const accounts = item?.config?.accounts || [];
        return [
          ...acc,
          ...accounts.map((account) => ({
            label: account.email || account.domain,
            value: account.email || account.domain,
            provider: item.provider,
            badge1: account.email ? 'email' : 'domain',
            badge2: item.provider,
          })),
        ];
      }, []);
      return list;
    },
  },
  methods: {
    // eslint-disable-next-line
    emitPayload: debounce(function (val) {
      this.$emit('update', {
        id: this.item.id,
        type: this.item.type,
        payload: this.payload,
      });
    }, 300),
    onEmailToChange(value) {
      this.payload.to = value.trim().split(',');
      this.emitPayload();
    },
  },
  watch: {
    item: {
      handler() {
        if (this.item) {
          this.payload = JSON.parse(JSON.stringify(this.item.payload));
          this.emailTo = (this.item.payload.to || []).join(',');
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
